import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Search, Pill, Plus, Minus, Calendar } from 'lucide-react';
import { SUPPLEMENT_CATEGORIES, getRecommendedSupplementCategories, getConcernLabel } from './constants';

const SupplementCard = ({ supplement, inCart, cartQty, addToCart, updateQuantity, language }) => {
  const suppId = supplement.item_id || supplement.id;
  
  return (
    <Card 
      className={`border overflow-hidden transition-all duration-300 ${
        inCart ? 'border-green-500 bg-green-50/50' : 'border-[#E7E5E4] hover:shadow-lg'
      }`}
      data-testid={`supplement-card-${suppId}`}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          <div className="w-16 h-16 bg-green-100 rounded-lg flex-shrink-0 flex items-center justify-center">
            <Pill className="w-8 h-8 text-green-600" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-[#0F172A] text-sm line-clamp-2">{supplement.name}</h3>
            
            <div className="flex flex-wrap gap-1 mt-1">
              {supplement.sub_categories?.slice(0, 2).map(cat => (
                <Badge key={cat} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  {cat}
                </Badge>
              ))}
            </div>
            
            <div className="flex items-center gap-1 mt-1 text-xs text-[#78716C]">
              <Calendar className="w-3 h-3" />
              <span>Cure: {supplement.cure_duration_days} {language === 'fr' ? 'jours' : 'days'}</span>
            </div>
            
            <div className="flex items-center justify-between mt-3">
              <span className="font-bold text-[#0F172A]">
                {supplement.price?.toLocaleString('fr-FR')} FCFA
              </span>
              
              {inCart ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(suppId, -1)}
                    className="w-7 h-7 rounded-full bg-[#F5F5F4] flex items-center justify-center hover:bg-[#E7E5E4]"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-6 text-center font-medium text-sm">{cartQty}</span>
                  <button
                    onClick={() => updateQuantity(suppId, 1)}
                    className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => addToCart(supplement)}
                  size="sm"
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {language === 'fr' ? 'Ajouter' : 'Add'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SupplementsTab = ({
  supplements,
  allRecommendations,
  recommendedSupplements,
  supplementSearch,
  setSupplementSearch,
  selectedSupplementCategory,
  setSelectedSupplementCategory,
  selectedConcern,
  supplementCart,
  addSupplementToCart,
  updateSupplementQuantity,
  addToCart,
  language
}) => {
  const isSupplementInCart = (item) => {
    const itemId = item.item_id || item.id;
    return supplementCart.some(cartItem => cartItem.item_id === itemId);
  };
  
  const getSupplementCartQuantity = (item) => {
    const itemId = item.item_id || item.id;
    return supplementCart.find(cartItem => cartItem.item_id === itemId)?.quantity || 0;
  };

  const filteredSupplements = supplements.filter(supp => {
    const matchesSearch = supp.name.toLowerCase().includes(supplementSearch.toLowerCase());
    const matchesCategory = selectedSupplementCategory === 'all' || 
                           supp.sub_categories?.includes(selectedSupplementCategory);
    return matchesSearch && matchesCategory;
  });

  const recommendedCategories = getRecommendedSupplementCategories(selectedConcern);

  return (
    <div className="space-y-4">
      {/* New recommendations from products collection */}
      {allRecommendations.complements?.length > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <span className="text-lg">⭐</span>
              {language === 'fr' ? 'Compléments recommandés pour ce diagnostic' : 'Recommended supplements for this diagnosis'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {allRecommendations.complements.slice(0, 4).map(product => (
                <Card key={product.id} className="bg-white border-green-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-3">
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-2 bg-green-50 rounded-full flex items-center justify-center">
                        <Pill className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="text-xs font-medium text-[#5D4E37] line-clamp-2 mb-1">{product.name}</p>
                      <p className="text-sm font-bold text-green-600">{product.price?.toLocaleString()} F</p>
                      <Button 
                        size="sm" 
                        className="mt-2 w-full bg-green-600 hover:bg-green-700 text-white text-xs h-7"
                        onClick={() => addToCart(product)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {language === 'fr' ? 'Ajouter' : 'Add'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Recommended supplements banner based on selected concern */}
      {selectedConcern !== 'all' && recommendedCategories.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Pill className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-green-800">
                {language === 'fr' 
                  ? `Compléments recommandés pour : ${getConcernLabel(selectedConcern, language)}`
                  : `Recommended supplements for: ${getConcernLabel(selectedConcern, language)}`
                }
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {recommendedCategories.map(catValue => {
                const cat = SUPPLEMENT_CATEGORIES.find(c => c.value === catValue);
                return cat ? (
                  <Badge 
                    key={catValue}
                    className="bg-green-100 text-green-700 cursor-pointer hover:bg-green-200"
                    onClick={() => setSelectedSupplementCategory(catValue)}
                  >
                    {language === 'fr' ? cat.label_fr : cat.label_en}
                  </Badge>
                ) : null;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Diagnosis-based recommendations (original) */}
      {recommendedSupplements.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Pill className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-800">
                {language === 'fr' ? 'Recommandés pour ce diagnostic' : 'Recommended for this diagnosis'}
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {recommendedSupplements.slice(0, 4).map(supp => (
                <Badge 
                  key={supp.id}
                  className="bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200"
                  onClick={() => addSupplementToCart(supp)}
                >
                  + {supp.name.split(' ')[0]} - {supp.price?.toLocaleString('fr-FR')} FCFA
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
          <Input
            placeholder={language === 'fr' ? 'Rechercher un complément...' : 'Search supplements...'}
            value={supplementSearch}
            onChange={(e) => setSupplementSearch(e.target.value)}
            className="pl-12 input-default"
            data-testid="supplement-search"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {SUPPLEMENT_CATEGORIES.map(cat => (
            <Button
              key={cat.value}
              variant={selectedSupplementCategory === cat.value ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedSupplementCategory(cat.value)}
              className={`rounded-full whitespace-nowrap ${
                selectedSupplementCategory === cat.value 
                  ? 'bg-green-500 hover:bg-green-600 text-white' 
                  : ''
              } ${
                recommendedCategories.includes(cat.value) && selectedSupplementCategory !== cat.value
                  ? 'border-green-400 text-green-700'
                  : ''
              }`}
            >
              {language === 'fr' ? cat.label_fr : cat.label_en}
              {recommendedCategories.includes(cat.value) && ' ⭐'}
            </Button>
          ))}
        </div>
      </div>

      {/* Supplements Grid */}
      {filteredSupplements.length === 0 ? (
        <div className="text-center py-12">
          <Pill className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
          <p className="text-[#78716C]">
            {language === 'fr' ? 'Aucun complément trouvé' : 'No supplements found'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSupplements.map(supplement => (
            <SupplementCard
              key={supplement.item_id || supplement.id}
              supplement={supplement}
              inCart={isSupplementInCart(supplement)}
              cartQty={getSupplementCartQuantity(supplement)}
              addToCart={addSupplementToCart}
              updateQuantity={updateSupplementQuantity}
              language={language}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SupplementsTab;
